import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { OemApplication } from '../features/oem/application';
import { PublicProposalPage } from '../features/public-proposal/public-proposal-page';
import { getActiveSession } from '../lib/auth';

const rootRoute = createRootRoute({ component: () => <Outlet /> });
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // TanStack Router uses a thrown Redirect response as its documented control flow.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/app' });
  },
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: OemApplication,
});
const proposalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/proposal/$publicToken',
  component: PublicProposalPage,
});
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  beforeLoad: async () => {
    // TanStack Router uses a thrown Redirect response as its documented control flow.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if ((await getActiveSession()) === null) throw redirect({ to: '/login' });
  },
  component: OemApplication,
});
const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: OemApplication,
});
const opportunitiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'opportunities',
  component: OemApplication,
});
const newOpportunityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'opportunities/new',
  component: OemApplication,
});
const opportunityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'opportunities/$opportunityId',
  component: OemApplication,
});

const opportunitySectionPaths = [
  'brief',
  'options',
  'formula',
  'bom',
  'costing',
  'compliance',
  'quotation',
  'activity',
] as const;
const opportunitySectionRoutes = opportunitySectionPaths.map((path) =>
  createRoute({
    getParentRoute: () => appRoute,
    path: `opportunities/$opportunityId/${path}`,
    component: OemApplication,
  }),
);

const approvalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'approvals',
  component: OemApplication,
});
const handoffsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'handoffs',
  component: OemApplication,
});
const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'reports',
  component: OemApplication,
});

const masterSections = [
  'ingredients',
  'packaging',
  'suppliers',
  'processes',
  'machines',
  'rooms',
  'pricing-policies',
] as const;
const masterRoutes = masterSections.map((section) =>
  createRoute({
    getParentRoute: () => appRoute,
    path: `master-data/${section}`,
    component: OemApplication,
  }),
);
const regulatorySections = [
  'sources',
  'rules',
  'claims',
  'labels',
  'facilities',
  'tests',
  'change-alerts',
] as const;
const regulatoryRoutes = regulatorySections.map((section) =>
  createRoute({
    getParentRoute: () => appRoute,
    path: `regulatory/${section}`,
    component: OemApplication,
  }),
);

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'settings',
  component: OemApplication,
});
const settingsUsersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'settings/users',
  component: OemApplication,
});
const settingsOrganizationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'settings/organization',
  component: OemApplication,
});
const settingsOfflineRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'settings/offline',
  component: OemApplication,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  proposalRoute,
  appRoute.addChildren([
    dashboardRoute,
    opportunitiesRoute,
    newOpportunityRoute,
    opportunityRoute,
    ...opportunitySectionRoutes,
    approvalsRoute,
    handoffsRoute,
    reportsRoute,
    ...masterRoutes,
    ...regulatoryRoutes,
    settingsRoute,
    settingsUsersRoute,
    settingsOrganizationRoute,
    settingsOfflineRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
